import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import dayjsTZ from "../../../utils/dayjs";
import AgGridComponent from "../../AG-grid/AgGridComponent";
import PunonjesitContext from "../../punonjesit/store/PunonjesitContext";
import getAgThemePreference from "../../../utils/getAgThemePreference";
import { colDefs } from "./colDefs";
import { requestDataWithParams } from "../../../helpers/API/RequestFactory";
import { FetchIcon } from "../../../assets/icons";
import RangeFilterModal from "../components/RangeFilterModal";

const CLOCKIN_TYPES_FIELD_ID = "e685cd30-b119-11ec-829e-112b583587c7";

function Raportet() {
  const { users, isDarkMode } = useSelector((state) => state.usersList);
  const { programFields } = useSelector((state) => state.programFields);

  const { employee } = useContext(PunonjesitContext);

  const [rowData, setRowData] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [activeFilter, setActiveFilter] = useState([]);
  const [fetchModalOpen, setFetchModalOpen] = useState(false);

  const fetchRange = async (initialFilter) => {
    const filters = initialFilter || activeFilter.filter(Boolean);
    if (filters.length !== 2) {
      message.error({
        content: "Ju lutem zgjidhni një periudhë të vlefshme!",
        key: "fetching",
        duration: 1.5,
      });
      return;
    }
    message.loading({
      key: "fetching",
      content: "Duke ngarkuar të dhënat...",
    });
    const [start, end] = filters;
    const data = await requestDataWithParams(
      "clocking",
      dayjsTZ(start).valueOf(),
      dayjsTZ(end).add(1, "day").valueOf(),
      {
        employeeId: employee?.employeeId,
        dayType: "HolidayRequest",
      }
    );

    const populatedData = (data || []).map((el) => {
      const username = el?.clockingLogs?.[0]?.username;
      const isId =
        !!username &&
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
          username
        );
      let createdBy;
      if (isId) {
        const user = users?.allUsers?.Items?.find(
          (user) => user?.userSub === username
        );
        if (user) {
          createdBy = `${user?.given_name || ""} ${user?.family_name || ""}`;
        }
      }
      return {
        ...el,
        createdBy: createdBy || el?.clockingLogs?.[0]?.username || "N/A",
      };
    });

    setRowData(populatedData);
    setFetchModalOpen(false);
    message.success({
      key: "fetching",
      content: "Të dhënat u përditësuan me sukses!",
      duration: 1.5,
    });
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const dayTypes = useMemo(() => {
    if (!programFields) return {};
    const idx = programFields.findIndex(
      (el) => el?.fieldId === CLOCKIN_TYPES_FIELD_ID
    );
    if (idx === -1) return {};
    const toReturn = {};
    programFields[idx]?.fieldOptions?.forEach((el) => {
      toReturn[el?.name] = el?.description;
    });
    return toReturn;
  }, [programFields]);

  const columnDefs = useMemo(() => {
    return colDefs({ dayTypes });
  }, [dayTypes]);

  const { foregroundColor, accentColor } = getAgThemePreference();

  const EXPORT_COLS = [
    {
      en: "approved",
      al: "Statusi i turnit",
      replace: {
        true: "I aprovuar",
        false: "I pa aprovuar",
      },
    },
    { en: "dayType", al: "Lloji i turnit", replace: dayTypes },
    { en: "createdAt", al: "Krijuar në", format: "DD/MM/YYYY" },
    { en: "clockInDate", al: "Data e turnit", format: "DD/MM/YYYY" },
    { en: "createdBy", al: "Krijuar nga" },
  ];

  useEffect(() => {
    if (!employee) return;
    if (rowData !== null) return;

    fetchRange([
      dayjsTZ().subtract(4, "month").startOf("month"),
      dayjsTZ().endOf("month"),
    ]);
  }, [rowData, employee]);

  //region RETURN
  return (
    <div className="globalPageWrapper">
      <AgGridComponent
        gridApi={gridApi}
        rowData={rowData}
        rowSelection={{ mode: "multiRow" }}
        onGridReady={onGridReady}
        columnDefs={columnDefs}
        headerProps={{
          items: {
            search: {
              handleClean: () => setActiveFilter([]),
            },
            icons: {
              filter: {
                filterKeys: [
                  {
                    key: "createdAt",
                    label: "Krijuar në",
                    type: "date-range",
                  },
                  {
                    key: "createdBy",
                    label: "Krijuar nga",
                    type: "string",
                  },
                ],
              },
              excel: { tableCols: EXPORT_COLS },
              pdf: { tableCols: EXPORT_COLS },
              print: { tableCols: EXPORT_COLS },
              modals: [
                {
                  icon: (
                    <FetchIcon
                      width={20}
                      height={20}
                      fill={isDarkMode ? foregroundColor : accentColor}
                    />
                  ),
                  title: "Filtro në muajt e mëparshëm",
                  onClick: () => setFetchModalOpen(true),
                },
              ],
            },
          },
          exportTitle: `Lejet - ${employee?.employeeFirstName} ${employee?.employeeLastName}`,
        }}
        counterProps={{
          className: "rowFlex",
          title: "Totali i lejeve (orë përfshirë pushimin)",
          data: (rowData || []).map((el) =>
            !!el.clockInDate && el.clockOutDate
              ? dayjsTZ(el.clockOutDate)
                  .diff(dayjsTZ(el.clockInDate), "hours", true)
                  .toFixed(2)
              : 0
          ),
        }}
        idKey={"clockingId"}
      />
      {fetchModalOpen ? (
        <RangeFilterModal
          open={fetchModalOpen}
          setOpen={setFetchModalOpen}
          activeFilter={activeFilter}
          onFetch={fetchRange}
          setActiveFilter={setActiveFilter}
        />
      ) : null}
    </div>
  );
}

export default Raportet;
